// Roles & permissions — the client mirror of the Go side's
// streamauth.RequireRole + RestrictResellers + the per-device
// reseller_grants. New role coming? Add it to KNOWN_ROLES and give it a
// row in ROLE_PERMISSIONS. New gated capability? Add a Permission and
// list it in the roles that should have it. Nav items + route guards +
// action buttons all consult `useCan(permission)` so the gate is one map.
//
// Resellers are special-cased: ROLE_PERMISSIONS.reseller is the bare
// floor (Search only), and `effectivePermissions()` *adds* fleet
// permissions for a reseller that an admin has handed device grants —
// "act like a support user, but only on these OLTs / routers". The
// per-device check (a row in the OLT/Routers/OLT-Issues lists, the ONU /
// router actions on the subscriber page) is `useCanOLT(name)` /
// `useCanRouter(name)`.

import { useAuth } from "./AuthContext";

const KNOWN_ROLES = ["admin", "support", "reseller"] as const;
export type Role = (typeof KNOWN_ROLES)[number];

/** Narrow the free-form `User.role` string from the backend to a known Role (or null). */
function asRole(s: string | undefined | null): Role | null {
  return s != null && (KNOWN_ROLES as readonly string[]).includes(s) ? (s as Role) : null;
}

const PERMISSIONS = [
  "dashboard:read",
  "subscribers:read",
  "subscribers:write",
  "olts:read",
  "olt:write",
  "routers:read",
  // "NOC operator surface" — the RADIUS portal admin pages (Status &
  // Config / RADIUS Issues / Box Chains). Admin + support only; a
  // reseller never gets this even with device grants (those pages aren't
  // reseller-scoped server-side, so they'd just 403).
  "radius:operate",
  "ipphone:read",
  "callblast:read",
  "sms:read",
  "bulksms:send",
  "messenger:reply",
  // The fibre-distribution surface (capacity dashboard, schematic, issues
  // page). Admin + support only — server side admits the same two roles
  // (`/v1/api/fiber/*` is not in resellerCan), so the gate is one map.
  "fiber:read",
  "admin:config",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS, // admin has everything
  support: [
    "dashboard:read",
    "subscribers:read",
    "subscribers:write",
    "olts:read",
    "olt:write",
    "routers:read",
    "radius:operate",
    "ipphone:read",
    "callblast:read",
    "sms:read",
    "bulksms:send",
    "messenger:reply",
    "fiber:read",
  ],
  // A reseller-role login is read-only and scoped to its own subscribers
  // (the server's streamauth.RestrictResellers middleware enforces it).
  // The floor is ONLY subscribers:read — which keeps Search + the
  // subscriber detail page. `effectivePermissions()` then *adds*
  // olts:read/olt:write (if the reseller has ≥1 OLT grant) and
  // routers:read (if ≥1 router grant), so the OLTs / OLT-Issues /
  // Routers pages light up — scoped, by the server, to the granted
  // devices — and the per-device actions on the subscriber page render.
  reseller: ["subscribers:read"],
};

/** A reseller-grants-bearing slice of the user (just what we need here). */
type GrantedUser = { role?: string; grant_olt_names?: string[]; grant_router_names?: string[] } | null | undefined;

/** The permissions a user *actually* has — the role's base set, plus the
 *  fleet permissions a granted reseller earns from its device grants. */
export function effectivePermissions(user: GrantedUser): ReadonlySet<Permission> {
  const role = asRole(user?.role);
  if (role == null) return new Set<Permission>();
  const out = new Set<Permission>(ROLE_PERMISSIONS[role]);
  if (role === "reseller") {
    if ((user?.grant_olt_names?.length ?? 0) > 0) {
      out.add("olts:read");
      out.add("olt:write");
    }
    if ((user?.grant_router_names?.length ?? 0) > 0) {
      out.add("routers:read");
    }
  }
  return out;
}

export function can(role: Role, perm: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(perm);
}

/** Current user's role, narrowed — null if anon or an unknown role string. */
export function useRole(): Role | null {
  return asRole(useAuth().user?.role);
}

/** Whether the current user is allowed to do `perm` — grant-aware for
 *  reseller accounts. False when anon / unknown role. */
export function useCan(perm: Permission): boolean {
  return effectivePermissions(useAuth().user).has(perm);
}

/** The OLT names this user may operate on: `null` ⇒ all (admin/support);
 *  an array ⇒ exactly these (a reseller's granted set; [] ⇒ none). */
export function useOLTGrants(): string[] | null {
  const user = useAuth().user;
  return asRole(user?.role) === "reseller" ? (user?.grant_olt_names ?? []) : null;
}
/** The router names this user may operate on: `null` ⇒ all (admin/support). */
export function useRouterGrants(): string[] | null {
  const user = useAuth().user;
  return asRole(user?.role) === "reseller" ? (user?.grant_router_names ?? []) : null;
}
/** May the current user act on the named OLT? (admin/support: always.) */
export function useCanOLT(name: string | undefined | null): boolean {
  const g = useOLTGrants();
  return g == null || (name != null && g.includes(name));
}
/** May the current user act on the named router? (admin/support: always.) */
export function useCanRouter(name: string | undefined | null): boolean {
  const g = useRouterGrants();
  return g == null || (name != null && g.includes(name));
}
